import React from 'react';
import AppButton from '@common/components/app-button';
import {ModalIds} from '@common/models/enums/modal-ids.enum';
import useModalWithData from '@common/hooks/use-modal-with-data';
import {ReactComponent as EyeIcon} from '@assets/icons/eye-1.svg';
import {useLoading} from "../../core/hooks/use-loading.hook";
import AppModal from "../../core/components/app-modal";
import {animated} from "@react-spring/web";
import {useModalAnimation} from "../../core/animations";

export interface PublishArticleModalData {
    articleId?: string;
    onConfirm?: () => void | Promise<void>;
    onViewInEditor?: () => void;
}

const PublishArticleModal: React.FC = () => {
    const {hideModal, data, isOpenModal,} = useModalWithData<PublishArticleModalData>(ModalIds.ARTICLE_PUBLISH_CONFIRM);
    const {isLoading} = useLoading();

    const {springProps, closeModal} = useModalAnimation(!!isOpenModal(), 'medium');

    const modalData = data as PublishArticleModalData | null;

    if (!modalData) return null;

    const {onConfirm, onViewInEditor} = modalData;

    const isPublishing = isLoading('USE_ARTICLE_API');

    const handleConfirm = async () => {
        try {
            await onConfirm?.();
            hideModal();
        } catch (error) {
            console.error('Błąd podczas publikowania artykułu:', error);
        }
    };

    const handleCancel = () => {
        closeModal( () => {
            hideModal();
        });
    };

    const handleViewInEditor = () => {
        closeModal( () => {
            hideModal();
            onViewInEditor?.();
        });
    };

    return (
        <AppModal
            id={ModalIds.ARTICLE_PUBLISH_CONFIRM}
            key={ModalIds.ARTICLE_PUBLISH_CONFIRM}
            zIndex={50}
            forceFixed={true}>
            <animated.div
                style={springProps}
                className="h-full w-full flex flex-col bg-surface-level-1 rounded-2xl border border-outline-mild shadow-lvl-2 py-4 px-6 md:max-w-md"
            >
                <div className="flex items-center gap-3 mb-4">
                    <div
                        className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <EyeIcon className="w-5 h-5 text-primary"/>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-on-surface">
                            Ustaw jako widoczny
                        </h3>
                        <p className="text-sm text-on-surface/70">
                            Artykuł zostanie opublikowany
                        </p>
                    </div>
                </div>

                <div className="mb-6 space-y-3">
                    <p className="text-on-surface/80 text-sm">
                        Ustawienie artykułu jako widocznego spowoduje jego opublikowanie, wprowadzi zmiany z wersji
                        roboczej i udostępni go użytkownikom.
                    </p>
                    <button
                        type="button"
                        onClick={handleViewInEditor}
                        className="text-sm text-primary font-semibold underline hover:text-primary/80 cursor-pointer"
                    >
                        Przejdź do edycji artykułu
                    </button>
                </div>

                <div className="flex gap-3 flex-wrap md:flex-nowrap">
                    <AppButton
                        onClick={handleCancel}
                        className="!w-full md:w-fit font-semibold border-2 border-on-surface hover:bg-on-surface/5"
                        disabled={isPublishing}
                    >
                        Anuluj
                    </AppButton>
                    <AppButton
                        onClick={handleConfirm}
                        className="!w-full md:w-fit bg-primary text-on-primary font-semibold hover:bg-primary/80"
                        disabled={isPublishing}
                        loading={isPublishing}
                        loaderColor="#fff"
                    >
                        {isPublishing ? 'Publikowanie...' : 'Ustaw jako widoczny'}
                    </AppButton>
                </div>
            </animated.div>
        </AppModal>
    );
};

export default PublishArticleModal;
